import React from "react";
import { useLang } from "@/context/LanguageContext";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PAGE_SIZE = 20;

// Slice a filtered list for the current page. Filtering happens upstream (server
// or memo) so this only governs how many rows are rendered at once.
export function paginate(list, page, size = PAGE_SIZE) {
  const arr = list || [];
  const total = arr.length;
  const pages = Math.max(1, Math.ceil(total / size));
  const cur = Math.min(Math.max(1, page), pages);
  return { pageItems: arr.slice((cur - 1) * size, cur * size), total, pages, page: cur };
}

export default function Pagination({ page, pages, total, onPage, size = PAGE_SIZE, testid = "pagination" }) {
  const { t } = useLang();
  if (!pages || pages <= 1) return null;

  const from = (page - 1) * size + 1;
  const to = Math.min(page * size, total);

  const nums = [];
  for (let p = 1; p <= pages; p++) {
    if (p === 1 || p === pages || (p >= page - 1 && p <= page + 1)) nums.push(p);
    else if (nums[nums.length - 1] !== "…") nums.push("…");
  }

  return (
    <nav className="mt-6 flex flex-wrap items-center justify-between gap-3" data-testid={testid} aria-label="pagination">
      <span className="text-xs text-slate-400" data-testid={`${testid}-summary`}>
        {t("pg.showing").replace("{from}", from).replace("{to}", to).replace("{total}", total)}
      </span>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1} data-testid={`${testid}-prev`}
          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-border text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
          <ChevronLeft size={15} /> {t("pg.prev")}
        </button>
        {nums.map((n, i) => n === "…" ? (
          <span key={`e${i}`} className="px-2 text-slate-400 select-none">…</span>
        ) : (
          <button type="button" key={n} onClick={() => onPage(n)} data-testid={`${testid}-page-${n}`}
            aria-current={n === page ? "page" : undefined}
            className={`h-9 min-w-9 px-2 rounded-lg text-sm font-semibold transition-colors ${
              n === page ? "findo-gradient text-white" : "border border-border text-slate-600 hover:bg-slate-50"
            }`}>
            {n}
          </button>
        ))}
        <button type="button" onClick={() => onPage(page + 1)} disabled={page >= pages} data-testid={`${testid}-next`}
          className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-border text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
          {t("pg.next")} <ChevronRight size={15} />
        </button>
      </div>
    </nav>
  );
}
